/**
 * 智能体类型定义
 *
 * 定义智能体相关的TypeScript类型，供前端组件、服务和状态管理使用
 */

// ==================== 工具定义 ====================

/** 只读工具列表 */
export const READ_TOOLS = [
  'chapter_list',
  'chapter_read',
  'abstract_read',
  'setting_list',
  'setting_read',
  'search',
  'neo4j',
] as const;

/** 写入工具列表 */
export const WRITE_TOOLS = [
  'chapter_write',
  'chapter_update',
  'setting_write',
  'setting_update',
  'title_rename',
] as const;

/** 网络工具列表 */
export const WEB_TOOLS = [
  'websearch',
  'web_fetch',
] as const;

/** 所有工具列表 */
export const ALL_TOOLS = [...READ_TOOLS, ...WRITE_TOOLS, ...WEB_TOOLS] as const;

/** 工具名称类型 */
export type ToolName = typeof ALL_TOOLS[number];

/** 工具类型 */
export type ToolCategory = 'read' | 'write' | 'web';

/** 工具信息 */
export interface ToolInfo {
  name: ToolName;
  description: string;
  category: ToolCategory;
}

/** 工具列表响应 */
export interface ToolsListResponse {
  tools: ToolInfo[];
  read_tools: ToolName[];
  write_tools: ToolName[];
}

// ==================== 智能体模式定义 ====================

/** 智能体模式类型 */
export type AgentMode = 'primary' | 'subagent' | 'all';

/** LLM 模型配置 */
export interface LlmConfig {
  model?: string;
  temperature?: number;
  max_tokens?: number;
  [key: string]: unknown;
}

// ==================== 智能体定义 ====================

/** 智能体信息 */
export interface Agent {
  id: number;
  user_id: number | null;
  name: string;
  prompt: string;
  temperature: number;
  enabled_tools: ToolName[];
  is_system: boolean;
  mode: AgentMode;
  llm_config: LlmConfig | null;
  enabled_skills: string[];
  subagent_invocation_timing: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** 创建智能体请求 */
export interface AgentCreate {
  name: string;
  prompt: string;
  temperature?: number;
  enabled_tools?: ToolName[];
  mode?: AgentMode;
  llm_config?: LlmConfig | null;
  enabled_skills?: string[];
  subagent_invocation_timing?: string | null;
}

/** 更新智能体请求 */
export interface AgentUpdate {
  name?: string;
  prompt?: string;
  temperature?: number;
  enabled_tools?: ToolName[];
  mode?: AgentMode;
  llm_config?: LlmConfig | null;
  enabled_skills?: string[];
  subagent_invocation_timing?: string | null;
}

// ==================== 子智能体定义 ====================

/** 子智能体信息 */
export interface SubagentInfo {
  id: number;
  name: string;
  mode: AgentMode;
  enabled_tools: ToolName[];
  subagent_invocation_timing: string | null;
  is_system: boolean;
}

/** 子智能体列表响应 */
export interface SubagentListResponse {
  subagents: SubagentInfo[];
  total: number;
}

/** 子智能体调用请求 */
export interface SubagentInvokeRequest {
  subagent_name: string;
  task: string;
  timeout?: number; // 默认60秒，最大300秒
}

/** 子智能体调用响应 */
export interface SubagentInvokeResponse {
  success: boolean;
  result: string | null;
  error: string | null;
  execution_time: number; // 执行时间（秒）
}

// ==================== WebSocket 消息类型 ====================

/** 智能体列表响应 */
export interface AgentListResponse {
  success: boolean;
  type: 'agent_list';
  data: Agent[];
  error?: string;
}

/** 获取智能体响应 */
export interface AgentGetResponse {
  success: boolean;
  type: 'agent_get';
  data: Agent;
  error?: string;
}

/** 创建智能体响应 */
export interface AgentCreateResponse {
  success: boolean;
  type: 'agent_create';
  data: Agent;
  error?: string;
}

/** 更新智能体响应 */
export interface AgentUpdateResponse {
  success: boolean;
  type: 'agent_update';
  data: Agent;
  error?: string;
}

/** 删除智能体响应 */
export interface AgentDeleteResponse {
  success: boolean;
  type: 'agent_delete';
  data?: { message: string };
  error?: string;
}

/** 获取工具列表响应 */
export interface AgentToolsListResponse {
  success: boolean;
  type: 'agent_tools_list';
  data: ToolsListResponse;
  error?: string;
}

// ==================== 类型守卫 ====================

/**
 * 验证是否为有效的工具名称
 */
export function isValidToolName(name: string): name is ToolName {
  return ALL_TOOLS.indexOf(name as ToolName) !== -1;
}

/**
 * 验证温度值是否有效
 */
export function isValidTemperature(value: number): boolean {
  return typeof value === 'number' && value >= 0 && value <= 2;
}

/**
 * 获取工具分类
 */
export function getToolCategory(toolName: ToolName): ToolCategory {
  if ((READ_TOOLS as readonly string[]).indexOf(toolName) !== -1) {
    return 'read';
  }
  if ((WRITE_TOOLS as readonly string[]).indexOf(toolName) !== -1) {
    return 'write';
  }
  if ((WEB_TOOLS as readonly string[]).indexOf(toolName) !== -1) {
    return 'web';
  }
  return 'read';
}

/**
 * 获取工具描述
 */
export function getToolDescription(toolName: ToolName): string {
  const descriptions: Record<ToolName, string> = {
    chapter_list: '列出章节列表',
    chapter_read: '读取章节内容',
    chapter_write: '写入新章节',
    chapter_update: '编辑已有章节',
    abstract_read: '读取章节摘要',
    setting_list: '列出设定列表',
    setting_read: '读取设定内容',
    setting_write: '新建设定',
    setting_update: '更新已有设定',
    title_rename: '重命名章节或设定标题',
    search: '统一检索：素材/设定/章节/摘要/记忆/技能文档',
    neo4j: '知识图谱查询',
    websearch: '网络搜索',
    web_fetch: '网页内容抓取',
  };
  return descriptions[toolName] || '';
}

/**
 * 获取所有工具信息
 */
export function getAllToolsInfo(): ToolInfo[] {
  return ALL_TOOLS.map(name => ({
    name,
    description: getToolDescription(name),
    category: getToolCategory(name),
  }));
}
